import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Sparkles, ArrowLeft, Filter, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSearchContext } from '@/contexts/SearchContext';
import { BotCard } from '@/components/BotCard';
import { GlobalSearchBar } from '@/components/search/GlobalSearchBar';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || '';
  
  const { 
    searchResults, 
    isSearching, 
    performSearch, 
    highlightText,
    query 
  } = useSearchContext();

  const [sortBy, setSortBy] = useState('relevance');
  const [priceFilter, setPriceFilter] = useState('all');

  useEffect(() => {
    if (queryParam && queryParam !== query) {
      performSearch(queryParam, categoryParam || undefined);
    }
  }, [queryParam, categoryParam, performSearch, query]);

  const filteredResults = React.useMemo(() => {
    if (!searchResults?.results) return [];
    
    let results = [...searchResults.results];

    // Apply price filter
    if (priceFilter !== 'all') {
      results = results.filter(bot => {
        const price = bot.price?.toLowerCase() || '';
        if (priceFilter === 'free') return price === 'free';
        if (priceFilter === 'freemium') return price === 'freemium';
        if (priceFilter === 'paid') return price !== 'free' && price !== 'freemium';
        return true;
      });
    }

    // Apply sorting
    results.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'popularity':
          return (b.popularity || 0) - (a.popularity || 0);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'relevance':
        default:
          return ((b as any).relevanceScore || 0) - ((a as any).relevanceScore || 0);
      }
    });

    return results;
  }, [searchResults, sortBy, priceFilter]);

  return (
    <>
      <SEOHead
        title={queryParam ? `Search: ${queryParam} - Vergo AI` : 'Search AI Tools - Vergo'}
        description={`Find the best AI tools for "${queryParam}". Browse ${searchResults?.totalResults || 0} results.`}
      />

      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon" className="shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <GlobalSearchBar className="flex-1" placeholder="Refine your search..." />
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Search Info */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">
                  {queryParam ? (
                    <>
                      Results for "<span className="text-primary">{queryParam}</span>"
                    </>
                  ) : (
                    'Search Results'
                  )}
                </h1>
                {!isSearching && searchResults && (
                  <p className="text-muted-foreground mt-1">
                    Found {searchResults.totalResults} AI tools
                    {searchResults.expandedTerms.length > 1 && (
                      <span className="ml-2">
                        (also searched: {searchResults.expandedTerms.slice(1).join(', ')})
                      </span>
                    )}
                  </p>
                )}
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2">
                <Select value={priceFilter} onValueChange={setPriceFilter}>
                  <SelectTrigger className="w-[130px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Price" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Prices</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="freemium">Freemium</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[140px]">
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Relevance</SelectItem>
                    <SelectItem value="rating">Rating</SelectItem>
                    <SelectItem value="popularity">Popularity</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Expanded Terms */}
            {searchResults?.expandedTerms && searchResults.expandedTerms.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI expanded:
                </span>
                {searchResults.expandedTerms.map(term => (
                  <Badge key={term} variant="secondary" className="text-xs">
                    {term}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Loading State */}
          {isSearching && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="h-48 w-full rounded-xl" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          )}

          {/* Results Grid */}
          {!isSearching && filteredResults.length > 0 && (
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {filteredResults.map((bot, index) => (
                <motion.div
                  key={bot.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <BotCard 
                    bot={{
                      ...bot,
                      // Highlight matched text in name and description
                      name: bot.name,
                      description: bot.description
                    }} 
                  />
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* No Results */}
          {!isSearching && searchResults && filteredResults.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
                <Search className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="text-2xl font-bold mb-2">No results found</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                We couldn't find any AI tools matching "{queryParam}". Try different keywords or browse our categories.
              </p>
              
              {/* Suggested Searches */}
              {searchResults.suggestions && searchResults.suggestions.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-3">Try searching for:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {searchResults.suggestions.map(suggestion => (
                      <Link key={suggestion} to={`/search?q=${encodeURIComponent(suggestion)}`}>
                        <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                          {suggestion}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/">
                  <Button variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
                <Link to="/deals">
                  <Button>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Browse Deals
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Related Suggestions */}
          {!isSearching && searchResults?.suggestions && searchResults.suggestions.length > 0 && filteredResults.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <h3 className="text-lg font-semibold mb-4">Related Searches</h3>
              <div className="flex flex-wrap gap-2">
                {searchResults.suggestions.map(suggestion => (
                  <Link key={suggestion} to={`/search?q=${encodeURIComponent(suggestion)}`}>
                    <Badge 
                      variant="outline" 
                      className="cursor-pointer hover:bg-primary/10 transition-colors"
                    >
                      <Search className="w-3 h-3 mr-1" />
                      {suggestion}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchResults;
